import {Component, EventEmitter, Input, Output} from '@angular/core';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {ComponentDetails} from '@emm/common-items/models/component.model';
import {Variation} from '@emm/common-items/models/variation.model';
import {Observable} from 'rxjs';
import {DialogService} from '@emm/shared/services/dialog.service';

@Component({
    selector: 'emm-components-card',
    templateUrl: './components-card.component.html',
    styleUrls: ['./components-card.component.scss'],
    standalone: false
})
export class ComponentsCardComponent {
    @Input() componentCardLabel: string;
    @Input() isLoading$: Observable<boolean>;
    private _components: ComponentDetails[] = [];

    constructor(private dialogService: DialogService) {}

    @Input() set components(value: ComponentDetails[] | string[] | null | undefined) {
        // Coerce falsy / non-array values to empty array
        const newComponents = Array.isArray(value) ? value as ComponentDetails[] : [];
        const tagsMatch = this.hasPendingChanges &&
            newComponents.length === this._components.length &&
            newComponents.every((comp, idx) =>
                this.getComponentTag(comp) === this.getComponentTag(this._components[idx])
            );

        this._components = newComponents;

        // Only reset pending changes if tags don't match our current state
        if (!tagsMatch) {
            this.hasPendingChanges = false;
            this.originalComponents = [];
            this.pendingOrderedTags = [];
            this.movedComponentTags.clear();
            this.lastMovedComponentTag = null;
            this.deletedComponentTags.clear();
        }
    }

    get components(): ComponentDetails[] {
        return this._components;
    }

    @Input() variation: Variation;
    @Input() itemName: string;
    @Input() isReadOnly = false;
    private _areComponentsInherited = false;
    @Input() set areComponentsInherited(value: boolean) {
        this._areComponentsInherited = !!value;
        if (this._areComponentsInherited) {
            this.forceResetPendingChanges();
        }
    }
    get areComponentsInherited(): boolean {
        return this._areComponentsInherited;
    }
    @Input() hideButtons = false;
    @Input() isSuperAdmin = false;
    @Input() isItem = false;
    @Input() isItemGrouping = false;
    @Input() isModifierGrouping = false;

    @Output() addComponent = new EventEmitter<void>();
    @Output() viewComponent = new EventEmitter<ComponentDetails | string>();
    @Output() reorderComponents = new EventEmitter<string[]>();
    @Output() deleteComponents = new EventEmitter<string[]>();

    hasPendingChanges = false;
    private originalComponents: ComponentDetails[] = [];
    private pendingOrderedTags: string[] = [];
    movedComponentTags = new Set<string>();
    lastMovedComponentTag: string | null = null;
    deletedComponentTags = new Set<string>();

    onAddComponent() {
        this.addComponent.emit();
    }

    onViewComponent(component: ComponentDetails | string) {
        this.viewComponent.emit(component);
    }

    onDrop(event: CdkDragDrop<ComponentDetails[]>) {
        if (event.previousIndex !== event.currentIndex) {
            // Save original order if this is the first change
            if (!this.hasPendingChanges) {
                this.originalComponents = [...this._components];
            }

            const componentsCopy = [...this._components];
            const movedComponent = componentsCopy[event.previousIndex];
            const movedComponentTag = this.getComponentTag(movedComponent);

            // If the component being moved was previously at a different position, keep it tracked
            if (this.movedComponentTags.has(movedComponentTag)) {
                this.movedComponentTags.delete(movedComponentTag);
            }

            moveItemInArray(componentsCopy, event.previousIndex, event.currentIndex);
            this._components = componentsCopy;

            // Track moved component by tag for stable visual feedback
            this.movedComponentTags.add(movedComponentTag);
            this.lastMovedComponentTag = movedComponentTag;
            this.pendingOrderedTags = componentsCopy.map(c => this.getComponentTag(c));
            this.hasPendingChanges = true;
        }
    }

    onDeleteComponent(component: ComponentDetails | string, event: Event) {
        event.stopPropagation();

        if (!this.hasPendingChanges) {
            this.originalComponents = [...this._components];
        }

        const componentTag = this.getComponentTag(component);
        this.deletedComponentTags.add(componentTag);
        this._components = this._components.filter(c => this.getComponentTag(c) !== componentTag);
        this.pendingOrderedTags = this._components.map(c => this.getComponentTag(c));
        this.hasPendingChanges = true;
        this.movedComponentTags.delete(componentTag);

        if (this.lastMovedComponentTag === componentTag) {
            this.lastMovedComponentTag = null;
        }
    }

    onSaveOrder() {
        if (this.hasPendingChanges) {
            const updatedTags = this._components.map(c => this.getComponentTag(c));

            // Show confirmation dialog only if there are deletions
            if (this.deletedComponentTags.size > 0) {
                const deletedComponentsList = Array.from(this.deletedComponentTags).join('<br>');
                const count = this.deletedComponentTags.size;
                const message = `Are you sure you want to remove ${count} component${count > 1 ? 's' : ''}?`;

                this.dialogService.openConfirmDialog(message, deletedComponentsList).afterClosed().subscribe(confirmed => {
                    if (confirmed) {
                        this.deleteComponents.emit(updatedTags);
                        this.forceResetPendingChanges();
                    }
                });
            } else if (this.pendingOrderedTags.length > 0) {
                // Reorder only - no confirmation needed
                this.reorderComponents.emit(this.pendingOrderedTags);
                this.forceResetPendingChanges();
            }
        }
    }

    private forceResetPendingChanges() {
        this.hasPendingChanges = false;
        this.originalComponents = [];
        this.pendingOrderedTags = [];
        this.movedComponentTags.clear();
        this.lastMovedComponentTag = null;
        this.deletedComponentTags.clear();
    }

    private resetPendingChanges() {
        this.forceResetPendingChanges();
    }

    onCancelReorder() {
        if (this.hasPendingChanges) {
            this._components = [...this.originalComponents];
            this.resetPendingChanges();
        }
    }

    get showAddButton(): boolean {
        return !this.hideButtons &&
            this.isSuperAdmin &&
            !this.areComponentsInherited &&
            (this.isItem || this.isItemGrouping || this.isModifierGrouping);
    }

    getComponentTag(component: ComponentDetails | string): string {
        if (typeof component === 'string') {
            return component;
        }
        return component?.tag ?? '';
    }

    isMovedComponent(component: ComponentDetails | string): boolean {
        const tag = this.getComponentTag(component);
        return this.movedComponentTags.has(tag) && this.lastMovedComponentTag !== tag;
    }

    isLastMovedComponent(component: ComponentDetails | string): boolean {
        const tag = this.getComponentTag(component);
        return this.lastMovedComponentTag === tag;
    }

    isDeletedComponent(component: ComponentDetails | string): boolean {
        const tag = this.getComponentTag(component);
        return this.deletedComponentTags.has(tag);
    }

    get changesSummary(): string {
        const parts: string[] = [];
        if (this.deletedComponentTags.size > 0) {
            parts.push(`${this.deletedComponentTags.size} deleted`);
        }
        if (this.movedComponentTags.size > 0) {
            parts.push(`${this.movedComponentTags.size} reordered`);
        }
        return parts.join(', ');
    }
}
